import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Icon, type IconName } from '@capsico/ui';
import type { TicketStatus } from '@capsico/contracts';
import type { BubbleView, PhotoSlot } from '../hooks/useSupport';

/*
 * Help + chat building blocks (W16). Presentation only: every label, value and handler comes from
 * the page, which takes them from the support hooks. Token classes only.
 */

const timeFormat = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});
const clockFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

/** "4:15 PM" today, "30 Oct, 4:15 PM" otherwise. */
export function supportTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toDateString() === new Date().toDateString()
    ? clockFormat.format(at)
    : timeFormat.format(at);
}

const STATUS_TONE: Record<TicketStatus, string> = {
  bot: 'bg-info-bg text-info',
  queued: 'bg-warning-bg text-warning',
  assigned: 'bg-brand-soft text-brand-ink',
  waiting_customer: 'bg-warning-bg text-warning',
  resolved: 'bg-positive-bg text-positive',
  closed: 'bg-surface-2 text-ink-2',
};

export function SupportStatusChip({ status, label }: { status: TicketStatus; label: string }) {
  return (
    <span
      className={`inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-semibold ${STATUS_TONE[status]}`}
    >
      {label}
    </span>
  );
}

/** "We kept what you typed — Keep / Discard". */
export function SupportDraftBar({
  text,
  keepLabel,
  discardLabel,
  onKeep,
  onDiscard,
}: {
  text: string;
  keepLabel: string;
  discardLabel: string;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-brand-soft px-3 py-2.5">
      <Icon name="refresh" size={16} />
      <p className="min-w-0 flex-1 text-xs text-ink">{text}</p>
      <button type="button" onClick={onDiscard} className="text-xs font-semibold text-ink-2">
        {discardLabel}
      </button>
      <button type="button" onClick={onKeep} className="text-xs font-semibold text-brand-ink">
        {keepLabel}
      </button>
    </div>
  );
}

/** Desk-hours / queue notice (off-hours: when the team replies). */
export function DeskNotice({
  tone,
  icon,
  title,
  body,
}: {
  tone: 'info' | 'warning' | 'positive';
  icon: IconName;
  title: string;
  body?: string | null;
}) {
  const bg = { info: 'bg-info-bg', warning: 'bg-warning-bg', positive: 'bg-positive-bg' }[tone];
  return (
    <div className={`flex items-start gap-3 rounded-xl px-4 py-3 ${bg}`}>
      <Icon name={icon} size={18} className="mt-0.5 shrink-0 text-ink" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {body && <p className="mt-0.5 text-xs text-ink-2">{body}</p>}
      </div>
    </div>
  );
}

/** Fixed-height ticket row (96 px incl. the gap) for My tickets — virtualised past 100 rows. */
export const TICKET_ROW_HEIGHT = 96;

export function TicketRow({
  number,
  reason,
  status,
  statusLabel,
  lastMessage,
  meta,
  unread,
  unreadLabel,
  onOpen,
}: {
  number: string;
  reason: string;
  status: TicketStatus;
  statusLabel: string;
  lastMessage: string | null;
  meta: string;
  unread: boolean;
  unreadLabel: string;
  onOpen: () => void;
}) {
  return (
    <div className="h-24 pb-3">
      <button
        type="button"
        onClick={onOpen}
        className="flex h-full w-full items-center gap-3 rounded-sheet border border-line bg-surface px-4 text-left active:bg-surface-2"
      >
        <span className="relative inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
          <Icon name="info" size={20} />
          {unread && (
            <span
              aria-label={unreadLabel}
              className="absolute -top-0.5 -right-0.5 size-3 rounded-full border-2 border-surface bg-negative"
            />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-bold text-ink">{reason}</span>
          </span>
          <span className="block truncate text-xs text-ink-2">{lastMessage ?? ' '}</span>
          <span className="block truncate text-xs text-ink-3 tabular">
            {number} · {meta}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <SupportStatusChip status={status} label={statusLabel} />
          <Icon name="chevron-right" size={16} className="text-ink-3" />
        </span>
      </button>
    </div>
  );
}

/** Radio list of reasons, grouped. */
export function ReasonGroups({
  groups,
  value,
  onPick,
  label,
}: {
  groups: { title: string; reasons: { code: string; label: string; hint: string | null }[] }[];
  value: string;
  onPick: (code: string) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="space-y-4">
      {groups.map((group) => (
        <section key={group.title}>
          <p className="mb-1.5 px-1 text-xs font-semibold tracking-wide text-ink-3 uppercase">
            {group.title}
          </p>
          <div className="divide-y divide-line overflow-hidden rounded-sheet border border-line bg-surface">
            {group.reasons.map((reason) => {
              const active = reason.code === value;
              return (
                <button
                  key={reason.code}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onPick(reason.code)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-surface-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-ink">{reason.label}</span>
                    {reason.hint && <span className="block text-xs text-ink-3">{reason.hint}</span>}
                  </span>
                  <span
                    className={`inline-flex size-5 shrink-0 items-center justify-center rounded-full ${
                      active ? 'bg-brand' : 'border-2 border-line-strong'
                    }`}
                  >
                    {active && <span className="size-2 rounded-full bg-ink" />}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/** Big one-tap reason buttons (rider trip issues). */
export function QuickReasons({
  reasons,
  busyCode,
  onPick,
}: {
  reasons: { code: string; label: string; hint: string | null; icon: IconName; urgent: boolean }[];
  busyCode: string | null;
  onPick: (code: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {reasons.map((reason) => (
        <button
          key={reason.code}
          type="button"
          disabled={busyCode !== null}
          onClick={() => onPick(reason.code)}
          className={`flex min-h-28 flex-col items-start justify-between gap-2 rounded-sheet border p-3 text-left disabled:opacity-60 ${
            reason.urgent ? 'border-negative bg-negative-bg' : 'border-line bg-surface'
          }`}
        >
          <span
            className={`inline-flex size-9 items-center justify-center rounded-full ${
              reason.urgent ? 'bg-negative text-white' : 'bg-brand-soft text-brand-ink'
            }`}
          >
            {busyCode === reason.code ? (
              <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Icon name={reason.icon} size={18} />
            )}
          </span>
          <span>
            <span className="block text-sm font-semibold text-ink">{reason.label}</span>
            {reason.hint && <span className="block text-xs text-ink-2">{reason.hint}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

/** "Which items?" — a − n + stepper per order line, capped at what was ordered. */
export function ItemPicker({
  title,
  hint,
  rows,
  onChange,
  labels,
}: {
  title: string;
  hint: string;
  rows: { id: string; name: string; ordered: number; picked: number; price: string }[];
  onChange: (id: string, quantity: number) => void;
  labels: { less: string; more: string; of: (picked: number, ordered: number) => string };
}) {
  return (
    <section>
      <p className="text-sm font-semibold text-ink">{title}</p>
      <p className="text-xs text-ink-3">{hint}</p>
      <div className="mt-2 divide-y divide-line overflow-hidden rounded-sheet border border-line bg-surface">
        {rows.map((row) => (
          <div key={row.id} className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{row.name}</p>
              <p className="text-xs text-ink-3 tabular">
                {labels.of(row.picked, row.ordered)} · {row.price}
              </p>
            </div>
            <div
              className={`inline-flex h-8 items-center rounded-lg ${row.picked > 0 ? 'bg-brand text-ink' : 'border border-line-strong text-ink'}`}
            >
              <button
                type="button"
                aria-label={labels.less}
                disabled={row.picked === 0}
                onClick={() => onChange(row.id, row.picked - 1)}
                className="inline-flex size-8 items-center justify-center disabled:opacity-40"
              >
                <Icon name="minus" size={14} />
              </button>
              <span className="w-5 text-center text-sm font-bold tabular">{row.picked}</span>
              <button
                type="button"
                aria-label={labels.more}
                disabled={row.picked >= row.ordered}
                onClick={() => onChange(row.id, row.picked + 1)}
                className="inline-flex size-8 items-center justify-center disabled:opacity-40"
              >
                <Icon name="plus" size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PhotoTiles({
  slots,
  onRemove,
  removeLabel,
  failedLabel,
  small = false,
}: {
  slots: PhotoSlot[];
  onRemove: (key: string) => void;
  removeLabel: string;
  failedLabel: string;
  small?: boolean;
}) {
  const size = small ? 'size-14' : 'size-20';
  return (
    <>
      {slots.map((slot) => (
        <div key={slot.key} className={`relative ${size} shrink-0`}>
          <img src={slot.preview} alt="" className={`${size} rounded-xl object-cover`} />
          {slot.progress !== null && (
            <div className="absolute inset-x-1.5 bottom-1.5 h-1.5 overflow-hidden rounded-full bg-surface">
              <div
                className="h-full bg-brand-hover transition-[width]"
                style={{ width: `${slot.progress}%` }}
              />
            </div>
          )}
          {slot.failed && (
            <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-negative-bg text-center text-xs font-semibold text-negative">
              {failedLabel}
            </span>
          )}
          {slot.progress === null && (
            <button
              type="button"
              aria-label={removeLabel}
              onClick={() => onRemove(slot.key)}
              className="absolute -top-1.5 -right-1.5 inline-flex size-6 items-center justify-center rounded-full bg-dark text-white"
            >
              <Icon name="x" size={12} />
            </button>
          )}
        </div>
      ))}
    </>
  );
}

function FileInput({
  inputRef,
  onFile,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  onFile: (file: File) => void;
}) {
  return (
    <input
      ref={inputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      capture="environment"
      className="hidden"
      onChange={(event) => {
        const file = event.target.files?.[0];
        if (file) onFile(file);
        event.target.value = '';
      }}
    />
  );
}

/** Photos for a new ticket: thumbnails with upload bars, plus the dashed add tile. */
export function PhotoPicker({
  title,
  hint,
  slots,
  canAdd,
  onFile,
  onRemove,
  labels,
}: {
  title: string;
  hint: string;
  slots: PhotoSlot[];
  canAdd: boolean;
  onFile: (file: File) => void;
  onRemove: (key: string) => void;
  labels: { add: string; remove: string; failed: string };
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <section>
      <p className="text-sm font-semibold text-ink">{title}</p>
      <p className="text-xs text-ink-3">{hint}</p>
      <FileInput inputRef={input} onFile={onFile} />
      <div className="mt-2 flex flex-wrap gap-3">
        <PhotoTiles
          slots={slots}
          onRemove={onRemove}
          removeLabel={labels.remove}
          failedLabel={labels.failed}
        />
        {canAdd && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="flex size-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-brand-hover bg-brand-soft text-xs font-semibold text-brand-ink"
          >
            <Icon name="camera" size={20} />
            {labels.add}
          </button>
        )}
      </div>
    </section>
  );
}

/** Status strip at the top of the chat: status, ETA or queue position, who is replying. */
export function TicketStatusCard({
  title,
  subtitle,
  status,
  statusLabel,
  lines,
  children,
}: {
  title: string;
  subtitle: string | null;
  status: TicketStatus;
  statusLabel: string;
  lines: string[];
  children?: ReactNode;
}) {
  return (
    <section className="rounded-sheet border border-line bg-surface p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold text-ink">{title}</p>
          {subtitle && <p className="truncate text-xs text-ink-3 tabular">{subtitle}</p>}
        </div>
        <SupportStatusChip status={status} label={statusLabel} />
      </div>
      {lines.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {lines.map((line) => (
            <p key={line} className="text-xs text-ink-2">
              {line}
            </p>
          ))}
        </div>
      )}
      {children}
    </section>
  );
}

/** Pill row (language switch). Active = black pill, white text. */
export function PillChoice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-ink-2">{label}</span>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={`inline-flex h-8 items-center rounded-full px-3.5 text-xs font-medium ${
            value === option.value
              ? 'bg-ink text-surface'
              : 'border border-line-strong bg-surface text-ink'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** The thread: yours right (soft yellow), the assistant and agents left (white), system centred. */
export function ChatThread({
  bubbles,
  emptyText,
  sendingLabel,
}: {
  bubbles: BubbleView[];
  emptyText: string;
  sendingLabel: string;
}) {
  const end = useRef<HTMLLIElement>(null);
  const count = bubbles.length;
  // Keep the newest message in view as the thread grows.
  useEffect(() => {
    if (count > 0) end.current?.scrollIntoView?.({ block: 'end' });
  }, [count]);
  if (count === 0) {
    return <p className="py-8 text-center text-sm text-ink-3">{emptyText}</p>;
  }
  return (
    <ol className="space-y-3" aria-live="polite">
      {bubbles.map((bubble) =>
        bubble.side === 'system' ? (
          <li key={bubble.id} className="px-6 text-center text-xs text-ink-3">
            {bubble.body}
          </li>
        ) : (
          <li
            key={bubble.id}
            className={`flex ${bubble.side === 'me' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-4/5 rounded-2xl px-3.5 py-2.5 ${
                bubble.side === 'me'
                  ? 'rounded-br-md bg-brand-soft text-ink'
                  : 'rounded-bl-md border border-line bg-surface text-ink'
              } ${bubble.pending ? 'opacity-70' : ''}`}
            >
              {bubble.who && (
                <p className="mb-0.5 text-xs font-semibold text-brand-ink">{bubble.who}</p>
              )}
              {bubble.body && (
                <p className="text-sm whitespace-pre-wrap break-words">{bubble.body}</p>
              )}
              {bubble.photos.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {bubble.photos.map((photo) => (
                    <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
                      <img src={photo.url} alt="" className="size-24 rounded-lg object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <p className="mt-1 text-right text-xs text-ink-3 tabular">
                {bubble.pending ? sendingLabel : supportTime(bubble.at)}
              </p>
            </div>
          </li>
        ),
      )}
      <li ref={end} aria-hidden="true" />
    </ol>
  );
}

/** Reply box pinned under the chat: photo button, text, send. No typing indicators. */
export function ChatComposer({
  text,
  onText,
  maxLength,
  canSend,
  sending,
  onSend,
  slots,
  canAttach,
  onFile,
  onRemove,
  labels,
}: {
  text: string;
  onText: (value: string) => void;
  maxLength: number;
  canSend: boolean;
  sending: boolean;
  onSend: () => void;
  slots: PhotoSlot[];
  canAttach: boolean;
  onFile: (file: File) => void;
  onRemove: (key: string) => void;
  labels: { placeholder: string; send: string; attach: string; remove: string; failed: string };
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      {slots.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pt-1.5">
          <PhotoTiles
            slots={slots}
            onRemove={onRemove}
            removeLabel={labels.remove}
            failedLabel={labels.failed}
            small
          />
        </div>
      )}
      <div className="flex items-end gap-2">
        <FileInput inputRef={input} onFile={onFile} />
        <button
          type="button"
          aria-label={labels.attach}
          disabled={!canAttach}
          onClick={() => input.current?.click()}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-line-strong bg-surface text-ink disabled:opacity-40"
        >
          <Icon name="camera" size={20} />
        </button>
        <textarea
          value={text}
          rows={1}
          maxLength={maxLength}
          placeholder={labels.placeholder}
          aria-label={labels.placeholder}
          onChange={(event) => onText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (canSend) onSend();
            }
          }}
          className="max-h-28 min-h-11 flex-1 resize-none rounded-2xl border border-line-strong bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-3 focus:border-ink focus:outline-none"
        />
        <button
          type="button"
          aria-label={labels.send}
          disabled={!canSend}
          onClick={onSend}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-brand text-ink disabled:bg-surface-2 disabled:text-ink-3"
        >
          {sending ? (
            <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Icon name="arrow-up" size={20} />
          )}
        </button>
      </div>
    </div>
  );
}

/** 1–5 stars + optional comment after the ticket is resolved. */
export function CsatCard({
  title,
  body,
  score,
  onScore,
  comment,
  onComment,
  canSubmit,
  busy,
  onSubmit,
  labels,
  draft,
}: {
  title: string;
  body: string;
  score: number;
  onScore: (score: number) => void;
  comment: string;
  onComment: (value: string) => void;
  canSubmit: boolean;
  busy: boolean;
  onSubmit: () => void;
  labels: { star: (n: number) => string; comment: string; submit: string };
  draft?: ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-sheet border border-line bg-surface p-4">
      {draft}
      <div>
        <p className="text-base font-bold text-ink">{title}</p>
        <p className="text-xs text-ink-2">{body}</p>
      </div>
      <div role="radiogroup" aria-label={title} className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={score === n}
            aria-label={labels.star(n)}
            onClick={() => onScore(n)}
            className={`inline-flex size-11 items-center justify-center rounded-full ${
              n <= score ? 'bg-brand text-ink' : 'border border-line-strong text-ink-3'
            }`}
          >
            <Icon name="star" size={20} />
          </button>
        ))}
      </div>
      {score > 0 && (
        <textarea
          value={comment}
          rows={2}
          maxLength={300}
          placeholder={labels.comment}
          aria-label={labels.comment}
          onChange={(event) => onComment(event.target.value)}
          className="w-full resize-none rounded-xl border border-line-strong bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-3 focus:border-ink focus:outline-none"
        />
      )}
      <button
        type="button"
        disabled={!canSubmit || busy}
        onClick={onSubmit}
        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand text-sm font-semibold text-ink disabled:bg-surface-2 disabled:text-ink-3"
      >
        {busy && (
          <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {labels.submit}
      </button>
    </section>
  );
}

/** "You rated 4 / 5" once the rating is in. */
export function RatedLine({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-positive-bg px-4 py-3 text-sm font-medium text-ink">
      <Icon name="star" size={16} className="text-positive" />
      {text}
    </div>
  );
}

/** Refund given on this ticket (partner / rider view, or when the order detail is not loaded). */
export function RefundLine({
  amount,
  caption,
  status,
}: {
  amount: string;
  caption: string;
  status: string;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-positive-bg text-positive">
        <Icon name="wallet" size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink tabular">{amount}</p>
        <p className="truncate text-xs text-ink-3">{caption}</p>
      </div>
      <span className="text-xs font-semibold text-ink-2">{status}</span>
    </div>
  );
}
